import Vue from 'vue';
import Vuex from 'vuex';
import * as modules from './modules';
import { CACHE_APP_VERSION_KEY, APP_VERSION_NUMBER, CACHE_TMPL_PAGE_TYPE } from '@/const/host';
import i18n, { ZH, ZH_HK, EN } from '@/locales';
import { miniSafeGet } from '@/utils/api';
import axios from '@/axios';
import { common } from '@/axios/interface';
import { buildMenuIndex } from './buildMenuIndex';

Vue.use(Vuex);

function flattenArray(arr) {
	return arr.reduce((acc, val) => (Array.isArray(val) ? acc.concat(flattenArray(val)) : acc.concat(val)), []);
}

// 1:PC 2:旧版H5 3:新版H5 4:侧边栏PC 5:(新)瀑布流
// 6:侧边导航 7:卡片移动 8:翻页卡片
const allTypes = [1, 2, 3, 4, 5, 6, 7, 8];
const createStoreTemplates = () => {
	return allTypes.reduce((acc, val) => {
		acc[val] = {
			baseData: {},
			commonData: {}
		};
		return acc;
	}, {});
};

export const MARKET_UNIT_MAP = {
	'SH': 'CNY',
	'SZ': 'CNY',
	'HK': 'HKD',
	'US': 'USD'
};

export const TEMPLATE_MAP = {
	flow: '1',
	flowX: '5',
	slide: '4',
	slideM: '6',
	surf: '8'
};

export const TEMPLATE_TITLE_MAP = {
	flow: '瀑布式首页',
	flowX: '新版瀑布式首页',
	slide: '侧边栏首页',
	slideM: '侧边导航首页',
	surf: '翻页卡片首页'
};

export default new Vuex.Store({
	state: {
		appVersion: null,
		stockSelectIndex: 0,

		accessMode: 'pc',
		accessSession: '',

		templates: {
			...createStoreTemplates()
		},
		activeTemplate: '1',
		activeModuleId: null,

		modulesConfig: {},

		initialsAreaCode: null,

		rebuild: {
			templateId: null,
			pageId: null,
			pageList: [],
			uiConfig: {},
			marketSource: {}
		}
	},
	getters: {
		getAppVersion(state) {
			if (state.appVersion) {
				return state.appVersion;
			}
			return sessionStorage.getItem(CACHE_APP_VERSION_KEY) || null;
		},
		getBaseData(state) {
			const baseData = state.templates[state.activeTemplate].baseData;
			return baseData;
		},
		getCommonData(state) {
			const commonData = state.templates[state.activeTemplate].commonData;
			return commonData;
		},

		isMobileMode(state) {
			return state.accessMode === 'mobile';
		},

		getNormalMenus(state, getters) {
			const { pageMenuVOS } = getters.getCommonData;
			const filterNormalMenus = (pageMenuVOS || []).filter(v => !v.isDefault);
			return filterNormalMenus;
		},
		getGlobalModules(state, getters) {
			const { pageMenuVOS } = getters.getCommonData;
			const filterGlobalMenus = (pageMenuVOS || []).filter(v => v.isDefault);
			const moduleGroups = (filterGlobalMenus || []).map(menu => menu.moduleVOS || []);
			const simplifiedModules = flattenArray(moduleGroups).map(module => {
				return {
					...module
				};
			});
			return simplifiedModules;
		},
		getPageAllModules(state, getters) {
			const { pageMenuVOS } = getters.getCommonData;
			const moduleGroups = (pageMenuVOS || []).map(menu => {
				const modules = menu.moduleVOS || [];
				return modules.map(module => ({
					...module,
					menuId: menu.id
				}));
			});
			const simplifiedModules = flattenArray(moduleGroups).map(module => {
				return {
					...module
				};
			});
			return simplifiedModules;
		},

		getIrPageType(state) {
			const getPageType = state.activeTemplate;
			if (getPageType) {
				return Number(getPageType);
			}
			const value = sessionStorage.getItem(CACHE_TMPL_PAGE_TYPE);
			return value ? Number(value) : null;
		},

		getIrMarketAndCode(state, getters) {
			const { code, market } = getters.getBaseData;
			return [market, code];
		},
		isHKStock(state, getters) {
			const [market] = getters.getIrMarketAndCode;
			if (!market) return false;
			return market.toLocaleUpperCase() == 'HK';
		},
		getCurrUnitTips(state, getters) {
			const [market] = getters.getIrMarketAndCode;
			return getters.isHKStock ? 'HKD' : ['SH', 'SZ'].includes(market.toLocaleUpperCase()) ? 'CNY' : 'USD';
		},

		getOrganizationName(state, getters) {
			const baseData = getters.getBaseData;
			const keyMap = {
				[ZH]: 'name',
				[ZH_HK]: 'nameZhCht',
				[EN]: 'nameEn'
			};
			const findKey = keyMap[i18n.locale];
			return miniSafeGet(baseData, findKey);
		},

		getInitialsAreaCode: state => state.initialsAreaCode,

		// -------------------- 重构待办
		getUiConfig: state => state.rebuild.uiConfig,
		getTemplateId: state => state.rebuild.templateId,
		getPageId: state => state.rebuild.pageId,
		getPageList: state => state.rebuild.pageList,
		getMarketSource: state => state.rebuild.marketSource
	},
	mutations: {
		resetVersion(state) {
			state.appVersion = APP_VERSION_NUMBER;
			sessionStorage.setItem(CACHE_APP_VERSION_KEY, APP_VERSION_NUMBER);
		},
		setVersion(state, data) {
			state.appVersion = data;
			sessionStorage.setItem(CACHE_APP_VERSION_KEY, data);
		},

		setAccessMode(state, isPC) {
			state.accessMode = isPC ? 'pc' : 'mobile';
		},
		setAccessSession(state, data) {
			state.accessSession = data;
		},

		setCommonData(state, { data = {}, overwrite = true }) {
			const preCommonData = this.getters.getCommonData;

			let realData;
			// 是否覆盖之前的信息 默认覆盖
			if (overwrite) {
				realData = { ...data };
			} else {
				realData = { ...preCommonData, ...data };
			}
			if (realData.pageMenuVOS) {
				realData.pageMenuVOS = realData.pageMenuVOS.map(menu => {
					return {
						...menu,
						id: menu.uuid || menu.id, // 新增了菜单唯一ID: uuid
						__id: menu.id
					};
				});
			}

			const pageData = realData;
			const { menuTree, moduleIdMap, uuidMap } = buildMenuIndex(realData?.pageMenuVOS || []);

			pageData.__menuTree = menuTree;
			pageData.__moduleIndexMap = moduleIdMap;
			pageData.__uuidMap = uuidMap;

			state.templates[state.activeTemplate].commonData = pageData;

			// 绑定颜色变量
			this.commit('storeTheme/bindAppThemeStyle', realData.themeColorsHex);
		},
		setColumnData(state, data = {}) {
			if (!data) return;
			state.templates[state.activeTemplate].baseData = data;
		},
		setStockSelectIndex(state, data) {
			state.stockSelectIndex = data;
		},

		setActiveTemplate(state, template) {
			if (state.templates[template]) {
				state.activeTemplate = template;
				sessionStorage.setItem(CACHE_TMPL_PAGE_TYPE, template);
			}
		},
		updateSelectModule(state, data) {
			state.activeModuleId = data;
		},

		updateModulesConfig(state, data) {
			const { moduleId, config } = data;
			state.modulesConfig[moduleId] = config || {};
		},

		updateInitialsAreaCode(state, areaCode) {
			state.initialsAreaCode = areaCode;
		},

		// -------------------- 重构待办
		setUiConfig(state, data) {
			state.rebuild.uiConfig = data;
		},
		setTemplateId(state, data) {
			state.rebuild.templateId = data;
		},
		setPageId(state, data) {
			state.rebuild.pageId = data;
		},
		setPageList(state, data) {
			state.rebuild.pageList = data;
		},
		setMarketSource(state, data) {
			state.rebuild.marketSource = data;
		}
	},
	actions: {
		switchTemplate({ commit }, template) {
			commit('setActiveTemplate', template);
		},
		getAreaCode: ({ commit }, callback) => {
			axios
				.post(common.initialsAreaCode, {}, { hideMessage: true })
				.then(res => {
					commit('updateInitialsAreaCode', res.data.data);
					callback(res.data.data);
				})
				.catch(err => {
					callback([]);
				});
		}
	},
	modules: { ...modules }
});
